import * as React from "react";
import { MoreHorizontal, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

// --- Types ---
export interface Column<T> {
  key: keyof T | string;
  header: React.ReactNode;
  render?: (row: T, index: number) => React.ReactNode;
  width?: string;
  className?: string;
}

export interface TableAction<T> {
  label: string | ((row: T) => string);
  onClick: (row: T, rowIndex: number) => void;
  onMouseEnter?: (row: T, rowIndex: number) => void;
  className?: string | ((row: T) => string);
  visible?: (row: T, rowIndex?: number) => boolean;
  icon?: (row: T) => React.ReactNode;
  variant?: "default" | "danger";
}

export interface CommonTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  actions?: TableAction<T>[];
  actionsWidth?: string;
  actionsHeader?: string;
  emptyMessage?: string;
  className?: string;
  tableClassName?: string;
}

// --- Primitive Components ---
export const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-x-auto">
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm text-left", className)}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      "text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 [&_tr]:border-b-0",
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(
      "divide-y divide-slate-100 dark:divide-slate-800/60 [&_tr:last-child]:border-0",
      className,
    )}
    {...props}
  />
));
TableBody.displayName = "TableBody";

export const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-slate-50/50 font-medium dark:bg-slate-800/50 [&>tr]:last:border-b-0",
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

export const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-colors data-[state=selected]:bg-purple-50/50 dark:data-[state=selected]:bg-purple-900/10",
      className,
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

export const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-11 px-6 py-3 font-semibold text-slate-600 dark:text-slate-300 align-middle",
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

export const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "px-6 py-4 align-middle text-slate-700 dark:text-slate-300 text-sm",
      className,
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

export const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-xs text-slate-500 dark:text-slate-400", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

// --- High-Level CommonTable Component ---
export function CommonTable<T extends object>({
  columns,
  data,
  loading = false,
  actions = [],
  actionsWidth = "120px",
  actionsHeader = "Actions",
  emptyMessage = "No records found.",
  className,
  tableClassName,
}: Readonly<CommonTableProps<T>>) {
  const hasActions = actions.length > 0;
  const actionColumnSpan = columns.length + (hasActions ? 1 : 0);

  return (
    <div
      className={cn(
        "w-full overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm border-0",
        className,
      )}
    >
      <table
        className={cn(
          "w-full text-sm text-left border-collapse",
          tableClassName,
        )}
      >
        <thead className="text-xs text-slate-500 uppercase bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
          <tr>
            {columns.map((col) => (
              <th
                key={String(col.key)}
                className={cn(
                  "px-6 py-3.5 font-semibold tracking-wider text-slate-600 dark:text-slate-300",
                  col.className,
                )}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.header}
              </th>
            ))}
            {hasActions && (
              <th
                className="px-6 py-3.5 font-semibold tracking-wider text-center text-slate-600 dark:text-slate-300"
                style={{ width: actionsWidth }}
              >
                {actionsHeader}
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
          {loading ? (
            <tr>
              <td
                colSpan={actionColumnSpan}
                className="px-6 py-12 text-center text-slate-500 dark:text-slate-400"
              >
                <div className="flex items-center justify-center gap-2 text-sm font-medium">
                  <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                  <span>Loading data...</span>
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={actionColumnSpan}
                className="px-6 py-12 text-center text-slate-500 dark:text-slate-400 text-sm"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr
                key={`row-${rowIndex}`}
                className="hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-colors group"
              >
                {columns.map((col) => (
                  <td
                    key={String(col.key)}
                    className={cn(
                      "px-6 py-4 text-slate-700 dark:text-slate-300 align-middle",
                      col.className,
                    )}
                  >
                    {col.render
                      ? col.render(row, rowIndex)
                      : String(
                          (row as Record<string, any>)[String(col.key)] ?? "",
                        )}
                  </td>
                ))}
                {hasActions && (
                  <td className="px-6 py-4 text-center align-middle">
                    {actions.some((action) =>
                      action.visible ? action.visible(row, rowIndex) : true,
                    ) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          render={
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                              aria-label="Actions menu"
                            />
                          }
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[160px]">
                          {actions
                            .filter((action) =>
                              action.visible
                                ? action.visible(row, rowIndex)
                                : true,
                            )
                            .map((action, actionIdx) => {
                              const labelText =
                                typeof action.label === "function"
                                  ? action.label(row)
                                  : action.label;
                              const extraClass =
                                typeof action.className === "function"
                                  ? action.className(row)
                                  : action.className || "";
                              const isDanger = action.variant === "danger";

                              return (
                                <DropdownMenuItem
                                  key={actionIdx}
                                  onClick={() => action.onClick(row, rowIndex)}
                                  onMouseEnter={() =>
                                    action.onMouseEnter?.(row, rowIndex)
                                  }
                                  className={cn(
                                    "p-2 cursor-pointer " +
                                      (isDanger &&
                                        "text-rose-600 focus:text-rose-600 focus:bg-rose-50 dark:focus:bg-rose-500/10"),
                                    extraClass,
                                  )}
                                >
                                  {action.icon && (
                                    <span className="mr-2">
                                      {action.icon(row)}
                                    </span>
                                  )}
                                  {labelText}
                                </DropdownMenuItem>
                              );
                            })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default CommonTable;
